import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { 
  CheckCircle2, Loader2, AlertTriangle, Lock, 
  RefreshCw, FileCheck, XCircle, Archive,
  ChevronDown, ChevronUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClosurePanel Component
 * 
 * Operator tool for formally closing an engagement (M7).
 * Only shown during REPORT_ISSUED state when M6 is complete.
 * 
 * Props:
 * - serId: Service Execution Record ID
 * - onClosureComplete: Callback when engagement is closed
 * - m6Complete: Whether M6 milestone is complete
 */
const ClosurePanel = ({ serId, onClosureComplete, m6Complete = false }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closureNotes, setClosureNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);

  const fetchStatus = async () => {
    if (!serId || !token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/engagements/${serId}/closure/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to fetch closure status');
      }
      
      const data = await response.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      console.error("Failed to load closure status:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [serId, token]);

  const handleCloseEngagement = async () => {
    if (!confirmClose) {
      setConfirmClose(true);
      return;
    }

    setClosing(true);
    try {
      const params = new URLSearchParams({
        closure_acknowledged: 'true'
      });
      if (closureNotes.trim()) {
        params.append('closure_notes', closureNotes.trim());
      }

      const response = await fetch(`${API_URL}/api/engagements/${serId}/close?${params}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data?.detail?.message || 'Failed to close engagement');
      }
      
      const data = await response.json();
      
      if (data.milestone_completed) {
        toast.success("Engagement closed successfully! M7 milestone completed. All 7 milestones are now complete.");
      } else {
        toast.success("Engagement closed successfully!");
      }
      
      setConfirmClose(false);
      
      // Refresh status
      await fetchStatus();
      onClosureComplete?.();
      
    } catch (err) {
      toast.error(err.message);
      setConfirmClose(false);
    } finally {
      setClosing(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="glass p-6 rounded-xl" data-testid="closure-panel">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-slate-400">Loading closure status...</span>
        </div>
      </div>
    );
  }

  // Show prerequisites message if M6 not complete
  if (!m6Complete) {
    return (
      <div className="glass p-6 rounded-xl border border-slate-700" data-testid="closure-panel">
        <div className="flex items-center gap-3 mb-4">
          <Archive className="w-5 h-5 text-slate-500" />
          <div>
            <h3 className="font-semibold text-slate-400">Service Closure</h3>
            <p className="text-sm text-slate-500">Prerequisites not met</p>
          </div>
        </div>
        <div className="p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-300">
              <p className="font-medium">Report must be issued first</p>
              <p className="text-amber-300/80 text-xs mt-1">
                Complete M6_REPORT_ISSUED before closing the engagement.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isClosed = status?.is_closed;
  const canClose = status?.can_close;

  return (
    <div className="glass p-6 rounded-xl" data-testid="closure-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Archive className={`w-5 h-5 ${isClosed ? 'text-green-400' : 'text-blue-400'}`} />
          <div>
            <h3 className="font-semibold text-white">Service Closure</h3>
            <p className="text-sm text-slate-400">
              {isClosed 
                ? "Engagement completed" 
                : "Formally close the engagement"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isClosed && (
            <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              M7 Complete
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchStatus}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg flex items-center gap-2">
          <XCircle className="w-4 h-4 text-red-400" />
          <span className="text-red-300 text-sm">{error}</span>
        </div>
      )}

      {/* Status Card */}
      <div className={`rounded-lg p-4 border mb-4 ${
        isClosed 
          ? 'bg-green-950/30 border-green-500/30' 
          : canClose 
            ? 'bg-blue-950/30 border-blue-500/30' 
            : 'bg-slate-800/50 border-slate-700'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isClosed ? (
              <CheckCircle2 className="w-4 h-4 text-green-400" />
            ) : canClose ? (
              <FileCheck className="w-4 h-4 text-blue-400" />
            ) : (
              <Lock className="w-4 h-4 text-slate-400" />
            )}
            <span className="text-sm font-medium text-white">
              {isClosed 
                ? "Engagement Closed" 
                : canClose 
                  ? "Ready to Close" 
                  : "Not Ready"
              }
            </span>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            isClosed 
              ? 'bg-green-500/20 text-green-400' 
              : canClose 
                ? 'bg-blue-500/20 text-blue-400' 
                : 'bg-slate-700 text-slate-400'
          }`}>
            {status?.current_state || 'UNKNOWN'}
          </span>
        </div>

        {/* Status Details */}
        <div className="flex gap-4 text-xs">
          <span className="text-slate-400">
            M6 Report: 
            <span className={status?.m6_complete ? 'text-green-400 ml-1' : 'text-amber-400 ml-1'}>
              {status?.m6_complete ? '✓ Issued' : '○ Pending'}
            </span>
          </span>
        </div>

        {/* Closure Record if exists */}
        {status?.closure_record && (
          <div className="mt-3 pt-3 border-t border-slate-700">
            <p className="text-xs text-slate-400 mb-1">Closure Record:</p>
            <div className="text-xs text-slate-300 space-y-1">
              <p>Closed at: {new Date(status.closure_record.closed_at).toLocaleString()}</p>
              {status.closure_record.closure_notes && (
                <p>Notes: {status.closure_record.closure_notes}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Closure Notes Input */}
      {canClose && !isClosed && (
        <div className="mb-4">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 mb-2"
          >
            {showNotes ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {showNotes ? "Hide notes" : "Add closure notes (optional)"}
          </button>
          
          {showNotes && (
            <textarea
              value={closureNotes}
              onChange={(e) => setClosureNotes(e.target.value)}
              placeholder="Optional notes about the engagement closure..."
              className="w-full p-3 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              rows={3}
              data-testid="closure-notes-input"
            />
          )}
        </div>
      )}

      {/* Action Button */}
      {!isClosed && canClose && (
        <Button
          onClick={handleCloseEngagement}
          disabled={closing}
          className={`w-full ${
            confirmClose 
              ? 'bg-red-600 hover:bg-red-700' 
              : 'bg-green-600 hover:bg-green-700'
          }`}
          data-testid="close-engagement-btn"
        >
          {closing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Closing...
            </>
          ) : confirmClose ? (
            <>
              <AlertTriangle className="w-4 h-4 mr-2" />
              Confirm Close Engagement
            </>
          ) : (
            <>
              <Archive className="w-4 h-4 mr-2" />
              Close Engagement
            </>
          )}
        </Button>
      )}

      {/* Confirmation Warning */}
      {confirmClose && !isClosed && (
        <div className="mt-4 p-3 bg-amber-900/30 border border-amber-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-300">
              <p className="font-medium mb-1">This action is final</p>
              <ul className="list-disc list-inside text-amber-300/80 space-y-1 text-xs">
                <li>The engagement will be marked as CLOSED</li>
                <li>M7 milestone will be completed</li>
                <li>All 7 milestones will be finalized</li>
              </ul>
              <button
                onClick={() => setConfirmClose(false)}
                className="text-amber-400 hover:text-amber-300 text-xs underline mt-2"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success State */}
      {isClosed && (
        <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-green-300">
              <p className="font-medium">Engagement Complete</p>
              <p className="text-green-300/80 text-xs mt-1">
                All 7 milestones are complete. The engagement has been formally closed 
                and the audit trail is finalized.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info Note */}
      {!isClosed && (
        <div className="mt-4 p-3 bg-slate-800/50 rounded-lg">
          <p className="text-xs text-slate-400">
            <Archive className="w-3 h-3 inline mr-1" />
            Closing the engagement completes the M7 milestone and finalizes 
            the audit trail. This is the final step in the engagement lifecycle.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClosurePanel;
